/**
 * 认证相关操作 Composable
 * @description 从 userStore 抽取的登录/登出/注册/Token 刷新逻辑
 */

import type { Ref } from 'vue'
import type {
  ApiResponse,
  LoginCredentials,
  LoginResponse,
  PhoneLoginCredentials,
  RegisterCredentials,
  RegisterResponse,
  UserInfo,
} from '@/types/auth'

import { STORAGE_KEYS } from '@/constants'
// 导入认证服务
import { authService } from '@/services/auth'
import { LoginType, UserRole } from '@/types/auth'

import { handleLoginError } from './authActions'

/**
 * 认证 Composable 的依赖接口
 */
export interface AuthComposableDeps {
  token: Ref<string | null>
  refreshToken: Ref<string | null>
  userInfo: Ref<UserInfo | null>
  loginLoading: Ref<boolean>
  loginType: Ref<LoginType>
  loginTime: Ref<number | null>
  /** Token 有效期（秒），从登录响应动态获取 */
  tokenExpiresIn: Ref<number | null>
  imageCaptcha: Ref<unknown>
  phoneCodeSending: Ref<boolean>
  phoneCodeCooldown: Ref<number>
  setToken: (newToken: string | null) => void
  setRefreshToken: (newRefreshToken: string | null) => void
  setUserInfo: (info: UserInfo | null) => void
  setPermissions: (permissions: string[]) => void
  setPermissionsFromUserInfo: (info: UserInfo | null, customPermissions?: string[]) => void
  setLoginType: (type: LoginType) => void
  setImageCaptcha: (captcha: unknown) => void
}

/**
 * 创建认证相关 actions
 */
/** Token 有效期默认值（秒），与后端 ACCESS_TOKEN_EXPIRE_MINUTES=60 一致 */
const DEFAULT_TOKEN_EXPIRES_IN_SECONDS = 3600

export function useAuthActions(deps: AuthComposableDeps) {
  const {
    token,
    refreshToken: refreshTokenRef,
    loginLoading,
    loginTime,
    tokenExpiresIn,
    setToken,
    setRefreshToken,
    setUserInfo,
    setPermissions,
    setPermissionsFromUserInfo,
    setLoginType,
    setImageCaptcha,
  } = deps

  // 密码登录（使用 AuthService 重构版）
  async function login(credentials: LoginCredentials): Promise<ApiResponse<LoginResponse>> {
    try {
      loginLoading.value = true
      setLoginType(LoginType.PASSWORD)

      // 使用 AuthService 统一处理 Mock/API 逻辑
      const response = await authService.login(credentials)

      if (response.code === '00000' && response.data) {
        // 使用 AuthService 规范化响应
        const normalized = authService.normalizeLoginResponse(response, credentials.username)

        if (normalized) {
          // 设置登录状态
          setUserInfo(normalized.userInfo)
          setPermissionsFromUserInfo(normalized.userInfo)
          setToken(normalized.token)
          if (normalized.refreshToken) {
            setRefreshToken(normalized.refreshToken)
          }
          // 保存 Token 有效期（秒），从后端响应动态获取
          if (response.data.expiresIn) {
            tokenExpiresIn.value = response.data.expiresIn
          }

          // 登录成功后尝试获取完整用户信息（含 role/permissions 等）
          try {
            const userResponse = await authService.getCurrentUser()
            if (userResponse.code === '00000' && userResponse.data?.role) {
              const enhancedUserInfo = {
                ...normalized.userInfo,
                ...userResponse.data,
                role: userResponse.data.role || UserRole.USER,
              }
              setUserInfo(enhancedUserInfo)
              setPermissionsFromUserInfo(enhancedUserInfo)
            }
          }
          catch (error: unknown) {
            // 获取完整用户信息失败，使用基础信息
            console.warn('[UserStore] 获取完整用户信息失败，使用基础信息:', error)
          }
        }
      }

      return response
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: null }
    }
    finally {
      loginLoading.value = false
    }
  }

  // 手机验证码登录（使用 AuthService 重构版）
  async function phoneLogin(credentials: PhoneLoginCredentials): Promise<ApiResponse<LoginResponse>> {
    try {
      loginLoading.value = true
      setLoginType(LoginType.PHONE)

      // 使用 AuthService 统一处理 Mock/API 逻辑
      const response = await authService.phoneLogin(credentials)

      if (response.code === '00000' && response.data) {
        const normalized = authService.normalizeLoginResponse(response, credentials.phone)

        if (normalized) {
          setUserInfo(normalized.userInfo)
          setPermissionsFromUserInfo(normalized.userInfo)
          setToken(normalized.token)
          if (normalized.refreshToken) {
            setRefreshToken(normalized.refreshToken)
          }
          // 保存 Token 有效期（秒），从后端响应动态获取
          if (response.data.expiresIn) {
            tokenExpiresIn.value = response.data.expiresIn
          }

          // 登录成功后尝试获取完整用户信息（含 role/permissions 等）
          try {
            const userResponse = await authService.getCurrentUser()
            if (userResponse.code === '00000' && userResponse.data?.role) {
              const enhancedUserInfo = {
                ...normalized.userInfo,
                ...userResponse.data,
                role: userResponse.data.role || UserRole.USER,
              }
              setUserInfo(enhancedUserInfo)
              setPermissionsFromUserInfo(enhancedUserInfo)
            }
          }
          catch (error: unknown) {
            // 手机登录：获取完整用户信息失败，使用基础信息
            console.warn('[UserStore] 手机登录：获取完整用户信息失败，使用基础信息:', error)
          }
        }
      }

      return response
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: null }
    }
    finally {
      loginLoading.value = false
    }
  }

  // 用户注册（使用 AuthService 重构版）
  async function register(credentials: RegisterCredentials): Promise<ApiResponse<RegisterResponse>> {
    try {
      loginLoading.value = true

      // 使用 AuthService 统一处理 Mock/API 逻辑
      const response = await authService.register(credentials)

      if (response.code === '00000' && response.data) {
        // 注册成功后不自动登录，让用户跳转到登录页手动登录
        // 避免"注册→跳登录页→检测已登录→再跳首页"的冗余交互
      }

      return response
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      return { code: 'B1000', msg: errorMsg, data: null }
    }
    finally {
      loginLoading.value = false
    }
  }

  // 登出
  async function logout() {
    try {
      loginLoading.value = true
      // 调用后端退出登录API
      await authService.logout()
    }
    catch (error) {
      console.error('[UserStore] 退出登录API调用失败:', error)
    }
    finally {
      // 无论API是否成功，都重置所有本地状态
      setToken(null)
      setRefreshToken(null)
      setUserInfo(null)
      setPermissions([])
      setImageCaptcha(null)
      loginLoading.value = false
      deps.phoneCodeSending.value = false
      deps.phoneCodeCooldown.value = 0
      deps.loginTime.value = null
      deps.tokenExpiresIn.value = null

      // 清除本地存储 - 使用持久化插件的key
      localStorage.removeItem(STORAGE_KEYS.USER_STORE_V2)
      localStorage.removeItem(STORAGE_KEYS.USER_STORE_V1) // 兼容清除遗留

      // 强制清除可能残留的其他相关存储
      localStorage.removeItem(STORAGE_KEYS.LEGACY_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.LEGACY_USER_INFO)
      localStorage.removeItem(STORAGE_KEYS.LEGACY_REFRESH_TOKEN)
      localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN)

      // 注意：不清除记住我相关的存储，让用户的偏好设置保持到下次登录
      // localStorage.removeItem(STORAGE_KEYS.REMEMBER_ME); // 保留用户的记住我偏好
      // localStorage.removeItem(STORAGE_KEYS.SAVED_CREDENTIALS); // 保留保存的凭据
    }
  }

  // 刷新token
  async function refreshAccessToken() {
    if (!refreshTokenRef.value) {
      return { success: false, error: 'No refresh token' }
    }

    try {
      const { authApi } = await import('@/api/auth')
      const response = await authApi.refreshToken(refreshTokenRef.value)

      if (response.code === '00000' && response.data) {
        setToken(response.data.token)
        setRefreshToken(response.data.refreshToken)
        return { success: true, data: response.data }
      }
      else {
        // 刷新失败，清除所有认证信息
        logout()
        return { success: false, error: response.msg }
      }
    }
    catch (error: unknown) {
      const errorMsg = handleLoginError(error)
      // 刷新失败，清除所有认证信息
      logout()
      return { success: false, error: errorMsg }
    }
  }

  // 检查token是否过期（从后端响应动态获取有效期）
  function isTokenExpired(): boolean {
    if (!token.value || !loginTime.value)
      return true

    // 使用从登录响应获取的 expiresIn（秒），回退默认值与后端一致
    const expiresInSeconds = tokenExpiresIn.value ?? DEFAULT_TOKEN_EXPIRES_IN_SECONDS
    const tokenExpireTime = loginTime.value + expiresInSeconds * 1000
    return Date.now() > tokenExpireTime
  }

  return {
    login,
    phoneLogin,
    register,
    logout,
    refreshAccessToken,
    isTokenExpired,
  }
}
